import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import MediaCarousel from "@/components/MediaCarousel";
import { DynIcon } from "@/lib/icons";
import { useStore } from "@/context/StoreContext";
import { effectivePrice, money, DEFAULT_TIERS, productVariants, unitPriceFor } from "@/lib/pricing";

const STOCK_STYLE = {
  "In Stock": "text-green-400",
  "Low": "text-yellow-400",
  "Sold Out": "text-red-500",
};

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const { addToCart } = useStore();
  const variants = productVariants(product);
  const [variant, setVariant] = useState(variants[0] || null);
  const price = unitPriceFor(product, variant);
  const onSale = variants.length === 0 && product.hot_steal && product.sale_price != null;
  const tiers = product.tiers && product.tiers.length ? product.tiers : DEFAULT_TIERS;
  const soldOut = product.stock === "Sold Out";

  const open = () => navigate(`/products/${product.id}`);

  return (
    <div
      className="bg-[#1C1C1C] border border-[#333] rounded-xl overflow-hidden flex flex-col hover:border-[#E60000] hover:shadow-[0_0_18px_rgba(230,0,0,0.3)] transition-all duration-300 fade-up"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative cursor-pointer" onClick={open}>
        <MediaCarousel media={product.media} heightClass="aspect-square" testid={`card-carousel-${product.id}`} />
        {product.hot_steal && (
          <span className="absolute top-2 left-2 bg-[#E60000] text-white text-[11px] font-heading font-bold uppercase px-2 py-1 rounded z-10 -skew-x-6">HOT STEAL</span>
        )}
        {product.stock && (
          <span className={`absolute top-2 right-2 bg-black/70 text-[11px] font-semibold px-2 py-1 rounded z-10 ${STOCK_STYLE[product.stock] || "text-white"}`}>{product.stock}</span>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-heading font-bold uppercase text-[#F0EDEA] text-base leading-tight cursor-pointer" onClick={open}>
          {product.name} <span className="text-[#A8A2A0] font-normal">#{product.code}</span>
        </h3>

        {variants.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`card-variants-${product.id}`}>
            {variants.map((v) => (
              <button
                key={v.label}
                onClick={() => setVariant(v)}
                data-testid={`card-variant-${product.id}-${v.label}`}
                className={`text-[11px] font-heading font-bold uppercase px-2.5 py-1 rounded-full border transition-colors ${variant?.label === v.label ? "border-[#E60000] bg-[#E60000] text-white" : "border-[#333] text-[#A8A2A0]"}`}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-3">
          <div className="eyebrow text-[10px]">Price</div>
          <div className="flex items-baseline gap-2">
            <span className="font-heading font-black text-2xl text-[#E60000]">{money(price)}</span>
            {onSale && <span className="text-[#A8A2A0] line-through text-sm">{money(product.price)}</span>}
          </div>
        </div>

        <div className="mt-3 bg-[#141414] border border-[#2a2a2a] rounded-lg p-3">
          <div className="eyebrow text-[10px] text-[#E60000] mb-1.5">Bundle Up & Save</div>
          <div className="space-y-1">
            {tiers.map((t, i) => (
              <div key={i} className="flex justify-between text-xs text-[#A8A2A0]">
                <span>{t.units}+ Units</span>
                <span className="text-[#F0EDEA] font-semibold">{money(t.discount)} OFF each</span>
              </div>
            ))}
          </div>
        </div>

        {product.badges && product.badges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {product.badges.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1 bg-[#141414] border border-[#2a2a2a] rounded-full px-2 py-1 text-[11px] text-[#F0EDEA]">
                <DynIcon name={b.icon} size={12} className="text-[#E60000]" /> {b.label}
              </span>
            ))}
          </div>
        )}

        {product.profile_nose && (
          <p className="mt-3 text-xs text-[#A8A2A0]"><span className="text-[#F0EDEA] font-semibold">Profile/Nose:</span> {product.profile_nose}</p>
        )}
        {product.summary && (
          <p className="mt-2 text-xs text-[#A8A2A0] line-clamp-3"><span className="text-[#F0EDEA] font-semibold">Summary:</span> {product.summary}</p>
        )}

        <div className="mt-auto pt-4">
          <button
            data-testid={`add-to-list-${product.id}`}
            disabled={soldOut}
            onClick={() => addToCart(product, 1, variant)}
            className="btn-skew btn-primary w-full text-sm"
          >
            <span>{soldOut ? "Sold Out" : "Add to List"}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
